import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Circle, Gem, Gift, Sparkles, Ticket } from "lucide-react";
import { GameModal } from "../../components/GameModal";
import { PublicLayout } from "../../components/PublicLayout";
import { StatusTag } from "../../components/StatusTag";
import { api } from "../../lib/api";

type WeeklyTaskStatus = "completed" | "available" | "not_eligible" | "locked";

interface WeeklyTask {
  task_code: string;
  title: string;
  description: string;
  action_label: string;
  status: WeeklyTaskStatus;
  status_label: string;
  completed_at?: string | null;
}

interface WeeklyReward {
  code: string;
  required_points: number;
  title: string;
  status: "granted" | "available" | "not_reached";
  status_label: string;
}

interface WeeklyPassStatus {
  is_graduated: boolean;
  is_open: boolean;
  is_available?: boolean;
  locked_reason?: string | null;
  graduate_week_end_at?: string | null;
  weekly_pass_available_at?: string | null;
  remaining_seconds?: number;
  display_message?: string;
  week_id: string;
  week_start_at: string;
  week_end_at: string;
  remaining_text: string;
  points: number;
  max_points: number;
  message?: string;
  tasks: WeeklyTask[];
  rewards: WeeklyReward[];
  next_task?: WeeklyTask | null;
  reward_summary?: string[];
}

const taskActions: Record<string, string | null> = {
  weekly_checkin: "/api/public/weekly-pass/checkin",
  weekly_deposit_1000: "/api/public/weekly-pass/check-deposit-task",
  weekly_red_packet_draw: "/api/public/weekly-pass/check-red-packet-draw-task",
  weekly_level_view: null,
  weekly_invite_view: null
};

export function WeeklyPassPage() {
  const account = localStorage.getItem("current_member_account") ?? localStorage.getItem("memberAccount") ?? "";
  const [status, setStatus] = useState<WeeklyPassStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTask, setActiveTask] = useState<string | null>(null);
  const [modal, setModal] = useState<{ title: string; text: string; tone?: "info" | "success" | "error" | "reward" } | null>(null);

  async function loadStatus() {
    if (!account) return;
    setStatus(await api<WeeklyPassStatus>(`/api/public/weekly-pass?member_account=${encodeURIComponent(account)}`));
  }

  useEffect(() => {
    loadStatus().catch((err) => {
      setModal({
        title: "讀取失敗",
        text: err instanceof Error ? err.message : "週通行證資料讀取失敗",
        tone: "error"
      });
    });
  }, [account]);

  const progress = useMemo(() => {
    const points = status?.points ?? 0;
    const max = status?.max_points ?? 5;
    return Math.max(0, Math.min(100, Math.round((points / max) * 100)));
  }, [status?.points, status?.max_points]);

  async function completeTask(task: WeeklyTask) {
    const endpoint = taskActions[task.task_code];
    if (!endpoint) {
      if (task.task_code === "weekly_level_view") window.location.href = "/level";
      if (task.task_code === "weekly_invite_view") window.location.href = "/invite";
      return;
    }
    setLoading(true);
    setActiveTask(task.task_code);
    try {
      await api(endpoint, {
        method: "POST",
        body: JSON.stringify({ member_account: account })
      });
      await loadStatus();
      setModal({ title: "任務完成", text: "週通行證任務已完成，點數與獎勵狀態已更新。", tone: "success" });
    } catch (err) {
      setModal({ title: "任務未完成", text: err instanceof Error ? err.message : "目前無法完成此任務，請稍後再試。", tone: "error" });
    } finally {
      setLoading(false);
      setActiveTask(null);
    }
  }

  return (
    <PublicLayout title="週通行證" subtitle="每週完成任務，累積點數領取獎勵">
      {!account ? (
        <section className="login-gate">
          <span className="hero-kicker">週通行證</span>
          <h1>請先登入會員帳號</h1>
          <p>登入會員後即可查看週通行證開啟狀態與每週任務。</p>
          <a className="primary-cta" href="/">返回龍來喬大廳</a>
        </section>
      ) : status?.is_available === false ? (
        <section className="weekly-pass-hero weekly-pass-locked">
          <div>
            <span className="hero-kicker">尚未開啟</span>
            <h1>週通行證即將開啟</h1>
            <p>畢業首週結束後，週通行證將於下一個週一 00:00 開啟。開啟後每週完成任務，可累積通行證點數並領取獎勵。</p>
            <div className="weekly-pass-locked-meta">
              <strong>畢業首週結束時間：{formatDateTime(status.graduate_week_end_at)}</strong>
              <strong>週通行證開啟時間：{formatDateTime(status.weekly_pass_available_at)}</strong>
              <strong>剩餘時間：{formatRemainingSeconds(status.remaining_seconds)}</strong>
              <strong>狀態：尚未開啟</strong>
            </div>
            <p className="graduate-week-hint">週通行證開啟後，每週一 00:00 重新開始新一輪任務。</p>
            <a className="primary-cta" href="/">返回龍來喬大廳</a>
          </div>
        </section>
      ) : (
        <>
          <section className="weekly-pass-hero">
            <div>
              <span className="hero-kicker">週通行證</span>
              <h1>本週點數 {status?.points ?? 0} / {status?.max_points ?? 5}</h1>
              <p>每週完成任務可累積通行證點數，領取寶石碎片、週耀紅包券與閃耀值。</p>
            </div>
            <div className="weekly-pass-stamp">
              <Ticket size={30} />
              <strong>{status?.remaining_text ?? "讀取中"}</strong>
            </div>
          </section>

          <section className="weekly-pass-overview">
            <div className="section-title-row">
              <h2>本週期間</h2>
              <span>{status?.week_id ?? "讀取中"}</span>
            </div>
            <div className="pass-bar"><span style={{ width: `${progress}%` }} /></div>
            <p>{status?.is_graduated ? `下一個任務：${status.next_task?.title ?? "本週任務已完成"}` : (status?.message ?? "完成新人七關後即可參與週通行證。")}</p>
          </section>

          <section className="weekly-pass-rewards">
            {status?.rewards?.map((reward) => (
              <article className={`weekly-reward-card ${reward.status}`} key={reward.code}>
                {reward.code === "reward_2_gem" ? <Gem size={22} /> : reward.code === "reward_3_voucher" ? <Gift size={22} /> : <Sparkles size={22} />}
                <div>
                  <span>{reward.required_points} 點</span>
                  <strong>{reward.title}</strong>
                </div>
                <StatusTag label={reward.status_label} tone={reward.status === "granted" ? "done" : reward.status === "available" ? "active" : "pending"} />
              </article>
            ))}
          </section>

          <section className="weekly-pass-task-list">
            <div className="section-title-row">
              <h2>本週任務</h2>
              <span>每項任務每週只能完成一次</span>
            </div>
            {(status?.tasks ?? []).map((task) => (
              <article className={`weekly-task-card ${task.status}`} key={task.task_code}>
                <div className="weekly-task-icon">
                  {task.status === "completed" ? <CheckCircle2 size={22} /> : <Circle size={22} />}
                </div>
                <div className="weekly-task-body">
                  <div className="quest-head">
                    <h3>{task.title}</h3>
                    <StatusTag label={task.status_label} tone={task.status === "completed" ? "done" : task.status === "available" ? "active" : "pending"} />
                  </div>
                  <p>{task.description}</p>
                  {task.completed_at ? <small>完成時間：{new Date(task.completed_at).toLocaleString("zh-TW", { hour12: false })}</small> : null}
                </div>
                <div className="task-action-stack">
                  {task.task_code === "weekly_checkin" ? (
                    <a className="secondary-cta task-guide-link" href="/group-checkin">查看打卡方式</a>
                  ) : null}
                  {task.status === "completed" ? (
                    <button className="secondary-cta" type="button" disabled>已完成</button>
                  ) : (
                    <button
                      className="primary-cta"
                      type="button"
                      disabled={!status?.is_graduated || loading || (task.status !== "available" && task.task_code !== "weekly_level_view" && task.task_code !== "weekly_invite_view")}
                      onClick={() => completeTask(task)}
                    >
                      {activeTask === task.task_code ? "處理中" : task.action_label}
                    </button>
                  )}
                </div>
              </article>
            ))}
          </section>
        </>
      )}
      <GameModal open={Boolean(modal)} title={modal?.title ?? ""} tone={modal?.tone ?? "info"} onClose={() => setModal(null)}>
        <p>{modal?.text}</p>
      </GameModal>
    </PublicLayout>
  );
}

function formatDateTime(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-TW", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
}

function formatRemainingSeconds(value?: number | null) {
  const seconds = Math.max(0, Number(value ?? 0));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days} 天 ${hours} 小時`;
  if (hours > 0) return `${hours} 小時 ${minutes} 分鐘`;
  if (minutes > 0) return `${minutes} 分鐘`;
  return "即將開啟";
}
